import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { useReactToPrint } from 'react-to-print';
import { Atividade, ItemSaldo, Safra, TratamentoSementes } from '../../@types';
import {
  AtividadesService,
  ItensSaldoService,
  PermissoesService,
  SafrasService,
  TratamentoSementesService,
} from '../../services';
import Agro1Toast from '../../components/Agro1Toast';
import SkeletonTable from '../../components/SkeletonTable';
import { SkeletonColumn } from '../../components/SkeletonTable/types';
import defaultColumnProps from '../../utils/default-column-props';
import humanizeDate from '../../utils/humanize-date';
import ActionButtons from './action-buttons';
import { TratamentoSementesToList } from './types';
import useLocalStore from '../../hooks/use-local-store';
import { FilterTratamentoSementesType } from '../../services/tratamento-sementes';
import { convertTratamentoSementesToList } from './utils';
import usePrintTratamentoSementeStore from './store';
import Printable from './printable';

const skeletonTableColumns: SkeletonColumn[] = [
  { header: '#' },
  { header: 'Data início' },
  { header: 'Data término' },
  { header: 'Atividade' },
  { header: 'Status' },
  { header: 'Tratamentos', className: 'col-4' },
  { header: 'Ações' },
];

const acoesField = (
  data: TratamentoSementes,
  listReloader: () => Promise<void>,
) => <ActionButtons data={data} listReloader={listReloader} />;

const statusParse = {
  aberto: { label: 'Aberto', className: 'aberto' },
  em_andamento: { label: 'Em andamento', className: 'em-andamento' },
  finalizado: { label: 'Finalizado', className: 'finalizado' },
} as const;

const statusField = (item: TratamentoSementesToList) => {
  const { label, className } = statusParse[item.status];

  return <div className={`status ${className}`}>{label}</div>;
};

const TratamentoSementesPage = () => {
  const navigate = useNavigate();

  const currentPropriedadeId = useLocalStore('currentPropriedade').value?.id;

  const [safraFilter, setSafraFilter] = useState<number>();
  const [safras, setSafras] = useState<Safra[]>([]);
  const dataToPrint = usePrintTratamentoSementeStore(
    (store) => store.dataToPrint,
  );
  const clearPrintData = usePrintTratamentoSementeStore((state) => state.clear);
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [sementesDeOrigem, setSementesDeOrigem] = useState<ItemSaldo[]>([]);
  const [requiredDataLoading, setRequiredDataLoading] =
    useState<boolean>(false);
  const [tratamentoSementes, setTratamentoSementes] = useState<
    TratamentoSementesToList[]
  >([]);
  const [tratamentosLoading, setTratamentosLoading] = useState<boolean>(false);

  const showLoading = requiredDataLoading || tratamentosLoading;
  const printRef = useRef(null);
  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => clearPrintData(),
  });

  const currentSafraId = useMemo(
    () => safras.find((safra) => safra.current)?.id,
    [safras],
  );

  const loadRequiredData = useCallback(async (propriedadeId: number) => {
    try {
      setRequiredDataLoading(true);

      const [atividadeList, sementeList, safraList] = await Promise.all([
        AtividadesService.list(),
        ItensSaldoService.saldos({
          operacaoItem: 'cultivar',
          propriedadeId,
          sementeValidaTratamento: true,
          noPaginate: true,
        }),
        SafrasService.list(),
      ]);

      setAtividades(atividadeList);
      setSementesDeOrigem(sementeList);
      setSafras(safraList);
    } catch (error) {
      Agro1Toast.show({
        detail: 'Falha ao buscar os dados da página.',
        severity: 'error',
        sticky: true,
      });
      console.error(error);
    } finally {
      setRequiredDataLoading(false);
    }
  }, []);

  const loadTratamentos = useCallback(
    async (params: FilterTratamentoSementesType) => {
      try {
        setTratamentosLoading(true);
        const newTratamentoSementes = convertTratamentoSementesToList(
          await TratamentoSementesService.list(params),
          {
            atividades,
            sementesDeOrigem,
          },
        );
        setTratamentoSementes(newTratamentoSementes);
      } catch (error) {
        Agro1Toast.show({
          detail: 'Falha ao buscar os tratamentos de semente.',
          severity: 'error',
          sticky: true,
        });
        console.error(error);
      } finally {
        setTratamentosLoading(false);
      }
    },
    [atividades, sementesDeOrigem],
  );

  const reloadData = useCallback(async () => {
    await loadTratamentos({
      propriedadeId: currentPropriedadeId,
      safraId: safraFilter ?? currentSafraId,
    });
  }, [currentPropriedadeId, currentSafraId, loadTratamentos, safraFilter]);

  const submitFilter = () => {
    if (!safraFilter) return;

    loadTratamentos({
      propriedadeId: currentPropriedadeId,
      safraId: safraFilter,
    });
  };

  useEffect(() => {
    if (!dataToPrint.tratamentos?.length) return;

    triggerPrint();
  }, [dataToPrint, triggerPrint]);

  useEffect(() => {
    if (!currentPropriedadeId) return;
    loadRequiredData(currentPropriedadeId);
  }, [currentPropriedadeId, loadRequiredData]);

  useEffect(() => {
    if (!currentPropriedadeId || !currentSafraId || requiredDataLoading) {
      return;
    }

    loadTratamentos({
      propriedadeId: currentPropriedadeId,
      safraId: currentSafraId,
    });
  }, [
    currentPropriedadeId,
    currentSafraId,
    loadTratamentos,
    requiredDataLoading,
  ]);

  useEffect(() => {
    setSafraFilter(currentSafraId);
  }, [currentSafraId]);

  return (
    <div className="tratamento-sementes-page">
      {/* Foi necessário colocar o ConfirmDialog aqui e não no action-buttons.tsx para ele não abrir multiplas vezes */}
      <ConfirmDialog />
      <div className="flex flex-wrap mt-6 mx-3">
        <div className="text-3xl">Tratamento de Sementes</div>
        {PermissoesService.tratamentoSemente.manage && (
          <Button
            className="button-secondary ml-auto"
            label="+ Novo"
            severity="success"
            onClick={() => navigate('/tratamento-sementes/create')}
          />
        )}
      </div>
      <hr />
      <div className="col-10 col-offset-1">
        <div className="flex flex-wrap align-items-center mt-5">
          <div>
            Safra:
            <Dropdown
              value={safraFilter}
              onChange={(event) => setSafraFilter(event.target.value)}
              options={safras}
              optionLabel="descricao"
              optionValue="id"
              placeholder="Safra"
              className="w-full md:w-20rem mx-2"
            />
          </div>
          <Button
            label="Filtrar"
            className="ml-3 button-primary"
            onClick={() => submitFilter()}
          />
        </div>
      </div>
      <hr />
      <div className="col-11 mt-5 m-auto">
        {showLoading && <SkeletonTable columns={skeletonTableColumns} />}
        {showLoading || (
          <DataTable
            value={tratamentoSementes}
            emptyMessage="Nenhum tratamento de sementes encontrado."
            paginator
            rows={10}
            className="mt p-0 data-table"
          >
            <Column field="id" header="#" className="col" />
            <Column
              {...defaultColumnProps()}
              header="Data Início"
              field="dataInicio"
              body={(tratamento: TratamentoSementesToList) =>
                humanizeDate(tratamento.dataInicio)
              }
            />
            <Column
              {...defaultColumnProps()}
              header="Data Término"
              field="dataFim"
              body={(tratamento: TratamentoSementesToList) =>
                humanizeDate(tratamento.dataFim)
              }
            />
            <Column
              {...defaultColumnProps('left')}
              header="Atividade"
              field="atividade.descricao"
            />
            <Column
              {...defaultColumnProps('left')}
              header="Status"
              field="status"
              body={statusField}
            />
            <Column
              {...defaultColumnProps('left')}
              header="Tratamentos"
              field="tratamentosParsed"
              className="col-4"
            />
            {PermissoesService.tratamentoSemente.manage && (
              <Column
                body={(item) => acoesField(item, reloadData)}
                header="Ações"
                className="col acoes"
                align="center"
              />
            )}
          </DataTable>
        )}
      </div>
      <div className="hidden-container">
        <Printable data={dataToPrint} ref={printRef} />
      </div>
    </div>
  );
};

export default TratamentoSementesPage;
